// Utils
import cntl from "cntl";

// Hooks
import { useState, useRef, useCallback, useEffect, useMemo, memo } from "react";

const inputRangeOuterCn = (className) => cntl`
  w-full
  ${className || ""}
`;

const inputRangeCn = () => cntl`
  w-full
  space-y-4
`;

const inputCn = () => cntl`
  h-1.5
  w-full
  border
  select-none
  rounded-full
  bg-black/[.05]
  cursor-pointer
  border-black/[.1]
`;

const barWrapperCn = () => cntl`
  inset-0
  absolute
  rounded-full
`;

const barCn = (isDisabled) => cntl`
  h-full
  relative
  rounded-full
  bg-primary-light-9
  dark:bg-primary-dark-9
  ${isDisabled ? "opacity-50" : ""}
`;

const inputAndBarCn = () => cntl`
  relative
`;

const indicatorBaseCn = () => cntl`
  border
  right-0
  top-1/2
  size-3.5
  absolute
  bg-white
  rounded-full
  shadow-light-sm
  translate-x-1/2
  -translate-y-1/2
  border-gray-light-8
  dark:border-gray-dark-8
`;

const percentageCn = () => cntl`
  mt-4
  relative
`;

const percentageTextCn = () => cntl`
  right-0
  text-sm
  absolute
  font-medium
  text-gray-light-12
  dark:text-gray-dark-12
`;

const labelCn = () => cntl`
  text-sm
  text-gray-light-12
  dark:text-gray-dark-12
`;

const disabledIndicatorCn = cntl`
  ${indicatorBaseCn()}
  cursor-not-allowed
  opacity-50
`;

const draggingIndicatorCn = cntl`
  ${indicatorBaseCn()}
  scale-110
  cursor-grabbing
  border-primary-light-9
  dark:border-primary-dark-9
`;

const defaultIndicatorCn = cntl`
  ${indicatorBaseCn()}
  cursor-grab
  hover:scale-105
  hover:border-primary-light-9
  dark:hover:border-primary-dark-9
`;

const getIndicatorClasses = (isDisabled, isDragging) => {
  if (isDisabled) return disabledIndicatorCn;
  if (isDragging) return draggingIndicatorCn;
  return defaultIndicatorCn;
};

const InputRange = memo(
  ({
    label,
    min = 0,
    step = 1,
    max = 100,
    className = "",
    initialValue = 25,
    isDisabled = false,
    onChange = () => {},
    showPercentage = true,
  }) => {
    const progressBarRef = useRef(null);

    const [value, setValue] = useState(initialValue);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const calculateValueFromPosition = useCallback(
      (clientX) => {
        if (!progressBarRef.current) return value;

        const rect = progressBarRef.current.getBoundingClientRect();
        const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
        const rawValue = (percentage / 100) * (max - min) + min;
        const steppedValue = Math.round(rawValue / step) * step;

        return Math.max(min, Math.min(max, steppedValue));
      },
      [max, min, step, value]
    );

    const updateValue = useCallback(
      (newValue) => {
        if (newValue !== value) {
          setValue(newValue);
          onChange(newValue);
        }
      },
      [value, onChange]
    );

    const handleMouseDown = useCallback(
      (e) => {
        if (isDisabled) return;

        e.preventDefault();
        setIsDragging(true);
        const newValue = calculateValueFromPosition(e.clientX);
        updateValue(newValue);
      },
      [isDisabled, calculateValueFromPosition, updateValue]
    );

    const handleMouseMove = useCallback(
      (e) => {
        if (!isDragging || isDisabled) return;

        const newValue = calculateValueFromPosition(e.clientX);
        updateValue(newValue);
      },
      [isDragging, isDisabled, calculateValueFromPosition, updateValue]
    );

    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
    }, []);

    const handleKeyDown = useCallback(
      (e) => {
        if (isDisabled) return;

        let newValue = value;

        switch (e.key) {
          case "ArrowLeft":
          case "ArrowDown":
            newValue = Math.max(min, value - step);
            break;
          case "ArrowRight":
          case "ArrowUp":
            newValue = Math.min(max, value + step);
            break;
          case "Home":
            newValue = min;
            break;
          case "End":
            newValue = max;
            break;
          case "PageDown":
            newValue = Math.max(min, value - step * 10);
            break;
          case "PageUp":
            newValue = Math.min(max, value + step * 10);
            break;
          default:
            return;
        }

        e.preventDefault();
        updateValue(newValue);
      },
      [isDisabled, value, min, max, step, updateValue]
    );

    const handleTouchStart = useCallback(
      (e) => {
        if (isDisabled) return;

        setIsDragging(true);
        const touch = e.touches[0];
        const newValue = calculateValueFromPosition(touch.clientX);
        updateValue(newValue);
      },
      [isDisabled, calculateValueFromPosition, updateValue]
    );

    const handleTouchMove = useCallback(
      (e) => {
        if (!isDragging || isDisabled) return;

        const touch = e.touches[0];
        const newValue = calculateValueFromPosition(touch.clientX);
        updateValue(newValue);
      },
      [isDragging, isDisabled, calculateValueFromPosition, updateValue]
    );

    useEffect(() => {
      if (!isDragging) return;

      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = () => handleMouseUp();
      const handleGlobalTouchMove = (e) => handleTouchMove(e);
      const handleGlobalTouchEnd = () => handleMouseUp();

      document.body.style.cursor = "grabbing";
      document.body.style.userSelect = "none";

      document.addEventListener("mousemove", handleGlobalMouseMove, { passive: false });
      document.addEventListener("mouseup", handleGlobalMouseUp, { passive: true });
      document.addEventListener("touchmove", handleGlobalTouchMove, { passive: false });
      document.addEventListener("touchend", handleGlobalTouchEnd, { passive: true });

      return () => {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchmove", handleGlobalTouchMove);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };
    }, [isDragging, handleMouseMove, handleTouchMove, handleMouseUp]);

    const percentage = useMemo(() => {
      return Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);
    }, [value, min, max]);

    const ariaProps = useMemo(
      () => ({
        role: "slider",
        "aria-label": `Range slider, current value ${value}`,
        "aria-valuemin": min,
        "aria-valuemax": max,
        "aria-valuenow": value,
        "aria-valuetext": `${value} (${Math.round(percentage)}%)`,
        "aria-disabled": isDisabled,
        tabIndex: isDisabled ? -1 : 0,
      }),
      [min, max, value, percentage, isDisabled]
    );

    const barStyle = useMemo(
      () => ({
        width: `${percentage}%`,
      }),
      [percentage]
    );

    return (
      <div className={inputRangeOuterCn(className)}>
        <div className={inputRangeCn()}>
          {label?.length > 0 && <div className={labelCn()}>{label}</div>}
          <div
            ref={progressBarRef}
            onKeyDown={handleKeyDown}
            className={inputAndBarCn()}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            {...ariaProps}
          >
            <div className={inputCn()} />
            <div className={barWrapperCn()}>
              <div className={barCn(isDisabled)} style={barStyle}>
                <div className={getIndicatorClasses(isDisabled, isDragging)}>
                  {showPercentage && (
                    <div className={percentageCn()}>
                      <div className={percentageTextCn()}>{Math.round(percentage)}%</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default InputRange;
